import { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import StyledWrapper from '../../style';
import Table from '../../../../components/Table/Table';
import ProcessTemplateColumns from '../../components/processTemplateColumns/processTemplateColumns';
import ButtonDropdown from '../../../../components/core/ButtonDropdown/ButtonDropdown';
import DeleteDialog from '../../../../components/DeleteDialog/DeleteDialog';
import StageTemplateCreation from '../../../StageTemplateCreationModal/StageTemplateCreation';
import { clearMetaInfo } from '../../../../components/FormBuilderLibrary/slice';

import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import Alert from '@mui/material/Alert';
import Collapse from '@mui/material/Collapse';
import CloseIcon from '@mui/icons-material/Close';
import useTemplates from '../../../../hooks/useTemplates';
import useActions from '../../hooks/useActions';

const ApplicationProcessTemplates = () => {
	const dispatch = useDispatch();
	const [searchParams, setSearchParams] = useSearchParams();
	const [columnWidth, setColumnWidth] = useState({});
	const [alertBalloon, setAlertBalloon] = useState(false);

	const handleAlert = (message, status) => {
		setAlertBalloon({ isOpen: true, message, status });
		setTimeout(() => {
			setAlertBalloon(false);
		}, 3000);
	};

	const initialColumns = ProcessTemplateColumns(columnWidth, handleAlert, true);
	const getTreeDataPath = (row) => row.hierarchy;

	const [openStageCreation, setOpenStageCreation] = useState(false);
	const [selectedRows, setSelectedRows] = useState([]);
	const [showValue, setShowValue] = useState('all');

	const tableRef = useRef(null);

	const { data, pagination } = useTemplates(showValue);

	const handleChange = (event) => {
		setShowValue(event.target.value);
	};

	useEffect(() => {
		const isStageCreation = searchParams.get('stageCreation');
		isStageCreation && setOpenStageCreation(isStageCreation);
	}, [searchParams]);
	const bulkActionButton = useActions('application-process', selectedRows, handleAlert, tableRef);

	const filteredColumns = initialColumns?.filter(
		(e) => e?.field !== 'linked_count' && e?.field !== 'computed_id'
	);

	return (
		<>
			<StyledWrapper.FormHeader>
				<StyledWrapper.LastEditedActions>
					<>
						<StyledWrapper.ShowDropdownContainer>
							Show
							<FormControl sx={{ m: 1, minWidth: 120 }} size="small">
								<Select
									id="show-templates-dropdown"
									value={showValue}
									onChange={handleChange}
									sx={{ border: 'red' }}
								>
									<MenuItem value={'all'}>All Templates</MenuItem>
									<MenuItem value={'active'}>Active Only</MenuItem>
									<MenuItem value={'archived'}>Archived Only</MenuItem>
								</Select>
							</FormControl>
						</StyledWrapper.ShowDropdownContainer>
						<StyledWrapper.ActionButtonContainer>
							<ButtonDropdown data={bulkActionButton} />
							<Button
								onClick={() => {
									dispatch(clearMetaInfo());
									setSearchParams({ ...searchParams, stageCreation: 'process' });
								}}
								size="small"
								data-testid={'templates-newTemplate-button'}
								variant="contained"
							>
								NEW APPLICATION PROCESS TEMPLATE
							</Button>
						</StyledWrapper.ActionButtonContainer>
					</>
				</StyledWrapper.LastEditedActions>
			</StyledWrapper.FormHeader>
			<Collapse in={alertBalloon}>
				<Alert
					severity={alertBalloon?.status}
					action={
						<IconButton
							aria-label="close"
							color={alertBalloon?.status}
							size="small"
							onClick={() => {
								setAlertBalloon(false);
							}}
						>
							<CloseIcon fontSize="inherit" />
						</IconButton>
					}
					sx={{ mb: 2, mt: 2 }}
				>
					{alertBalloon?.message}
				</Alert>
			</Collapse>

			<Table
				customHeight={'64vh'}
				columns={filteredColumns}
				rowHeight={true}
				enableAutoPageSize
				rows={data || []}
				rowCount={pagination?.rowCount}
				pagination={pagination?.pagination}
				pageSize={pagination?.pageSize}
				paginationMode={pagination?.paginationMode}
				onPageChange={(e) => pagination?.onPageChange(e)}
				onPageSizeChange={(e) => pagination?.onPageSizeChange(e)}
				page={pagination?.page}
				loading={pagination?.loading}
				sortingMode={pagination?.sortingMode}
				onSortModelChange={(e) => pagination?.onSortModelChange(e)}
				onFilterModelChange={(e) => pagination?.onFilterModelChange(e)}
				onSelectChange={(e) => setSelectedRows(e)}
				disableSelectionOnClick
				key={'BulkRevToEvalModal'}
				treeData
				isRowSelectable={(params) => !params?.row?.type?.includes('Stage')}
				getTreeDataPath={getTreeDataPath}
				groupingColDef={() => ({
					resizable: false,
					minWidth: 0,
					width: 0
				})}
				checkboxSelection
				onColumnResize={(e) => {
					const newColumnSizes = e?.colDef?.field;
					const width = e?.width;
					setColumnWidth({ ...columnWidth, [newColumnSizes]: width });
				}}
				getRowId={(row) => row.hierarchy.join('')}
				keepNonExistentRowsSelected
				hideDisabledCheckboxes
				addGroupDividers
				filterModel={pagination?.filterModel}
				sortModel={pagination?.sortModel}
				excludeColumns={['action', 'baseForms', 'adHocForms', 'linked_count']}
				ref={tableRef}
			/>

			{openStageCreation && (
				<StageTemplateCreation
					openModal={openStageCreation}
					isTemplate
					closeModal={() => setOpenStageCreation(false)}
				/>
			)}

			<DeleteDialog
				refetch={pagination?.refreshCall}
				selectedRows={selectedRows}
				handleAlert={handleAlert}
				props={{ type: 'Application process', is_template: true }}
			/>
		</>
	);
};

export default ApplicationProcessTemplates;
